import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { ThemedText, Button, MapLinksDropdown } from '../components';
import { Layout } from '../components/Layout';
import { DynamicFormRenderer } from '../components/FormBuilder';
import { formSchemaService, formSubmissionService, workOrderService } from '../api';
import { convertFormSchemaDefinitionToFields } from '../utils/schemaConverter';
import { validateField } from '../utils/fieldValidation';
import { evaluateRules } from '../utils/ruleEvaluator';
import { MapPin, Save, Info } from 'lucide-react';
import { renderFormIcon } from '../utils/iconUtils';
import { draftStorageService } from '../services/draftStorageService';
import { useAutoSaveDraft } from '../hooks/useAutoSaveDraft';
import { useUnsavedChangesWarning } from '../hooks/useUnsavedChangesWarning';
import useOrganizationConfig from '../hooks/useOrganizationConfig';
import { useRole } from '../hooks/useRole';
import LocationEditorMap from '../components/LocationEditorMap';

const PageContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${props => props.theme.spacing.xl};
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.md};
`;

const HeaderLeft = styled.div`
  flex: 1;
`;

const HeaderRight = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  align-items: center;
`;

const FormCard = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const InfoBox = styled.div`
  background-color: ${props => props.theme.colors.tint}15;
  border: 1px solid ${props => props.theme.colors.tint};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing.xs} 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.xl};
  padding-top: ${props => props.theme.spacing.xl};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  color: ${props => props.theme.colors.muted};
`;

const FormInfoBanner = styled.div`
  background: ${props => props.theme.isDark
    ? `linear-gradient(135deg, ${props.theme.colors.tint}, ${props.theme.colors.tint}80)`
    : 'linear-gradient(304deg, #444aff -19%, #232562, #0f1229 100%)'};
  color: white;
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.borderRadius.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
`;

const FormIconLarge = styled.div`
  font-size: 48px;
`;

const MiniMap = styled.div`
  width: 100%;
  height: 200px;
  border-radius: ${props => props.theme.borderRadius.md};
  overflow: hidden;
  margin-top: ${props => props.theme.spacing.sm};
  position: relative;
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
`;

const MapPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.muted};
`;

const DraftModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const DraftModal = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  max-width: 500px;
  width: 90%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
`;

const DraftModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const DraftIcon = styled.div`
  font-size: 48px;
`;

const DraftModalBody = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const DraftModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.md};
`;

const InfoText = styled(ThemedText)`
  font-size: 14px;
  opacity: 0.8;
  margin-top: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.sm};
  background-color: ${props => props.theme.colors.backgroundTertiary};
  border-radius: ${props => props.theme.borderRadius.sm};
`;

const WorkOrderSelect = styled.select`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  min-height: 40px;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
  }
`;

const MetaInput = styled.input`
  width: 100%;
  max-width: 300px;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  min-height: 40px;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
  }
`;

// Format a Date as a value for <input type="datetime-local"> ("YYYY-MM-DDTHH:MM", local time).
const toLocalInputValue = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const CreateSubmissionPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { user } = useAuth();
  const { isAdmin } = useRole();
  const { formId, sessionGuid } = useParams();
  const {
    enablePortalFormSubmission,
    allowEditSubmissionLocation,
    allowEditSubmissionDateTime,
    allowEditSubmissionSubmitter,
  } = useOrganizationConfig();
  // Submitter editing is admin-only (mirrors the server-side rule).
  const canEditSubmitter = allowEditSubmissionSubmitter && isAdmin();

  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(null);
  const [formSchema, setFormSchema] = useState([]);
  const [submissionFormData, setSubmissionFormData] = useState({});
  const [location, setLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formStartTime] = useState(Date.now()); // Track when form was loaded
  const [isDirty, setIsDirty] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [draftToRestore, setDraftToRestore] = useState(null);
  const [draftTimestampToShow, setDraftTimestampToShow] = useState(null);
  const [draftSessionGuid, setDraftSessionGuid] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState(
    routerLocation.state?.workOrderId || ''
  );
  // Editable submission metadata (used only when the org enables the matching flag).
  const [submitterName, setSubmitterName] = useState('');
  const [submitterEmail, setSubmitterEmail] = useState('');
  const [submittedAtLocal, setSubmittedAtLocal] = useState(() => toLocalInputValue(new Date()));

  // Seed the editable submitter from the current user once it's available.
  useEffect(() => {
    setSubmitterName(user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim());
    setSubmitterEmail(user?.email || '');
  }, [user]);

  // Auto-save draft every 3 seconds
  useAutoSaveDraft(sessionGuid, formId, submissionFormData, 3000, isDirty && sessionGuid);

  // Warn about unsaved changes
  useUnsavedChangesWarning(isDirty);

  // Track changes in form data
  useEffect(() => {
    if (Object.keys(submissionFormData).length > 0) {
      setIsDirty(true);
    }
  }, [submissionFormData]);

  // Draft check logic - runs when sessionGuid or formSchema changes
  useEffect(() => {
    // Wait for schema to be loaded
    if (formSchema.length === 0) {
      console.log('⏸️ Draft check skipped: schema not loaded yet');
      return;
    }

    // Initialize form data with default values for all fields
    const initialFormData = {};
    formSchema.forEach(field => {
      // Skip layout-only fields (no data storage needed)
      if (field.type === 'header' || field.type === 'divider' || field.type === 'spacer') {
        return;
      }

      // Initialize boolean fields with false
      if (field.type === 'toggle' || field.type === 'checkbox') {
        initialFormData[field.id] = false;
      }
      // Initialize array-based fields with empty array
      else if (field.type === 'checkbox_group' || field.type === 'tags') {
        initialFormData[field.id] = [];
      }
      // Skip file/image/signature/voice fields - won't be in draft (blob URLs don't persist)
      else if (field.type === 'image_camera' || field.type === 'drawing' ||
               field.type === 'file' || field.type === 'signature' || field.type === 'voice') {
        // Leave undefined - files don't persist in drafts
      }
      // Skip repeated forms - handled separately
      else if (field.type === 'repeated_form') {
        // Already initialized as empty array elsewhere
      }
      // Initialize all other input fields with empty string
      else {
        initialFormData[field.id] = '';
      }
    });

    console.log('🔍 DRAFT CHECK:', {
      sessionGuid,
      formId,
      initialFormDataKeys: Object.keys(initialFormData),
      hasSessionGuid: !!sessionGuid
    });

    if (sessionGuid) {
      console.log('✨ Session GUID found in URL, loading draft...');
      // We have a GUID in URL - load that specific draft
      const draft = draftStorageService.getDraft(sessionGuid);
      if (draft) {
        const mergedData = { ...initialFormData, ...draft };
        console.log('✅ Restoring draft data:', {
          initialKeys: Object.keys(initialFormData),
          draftKeys: Object.keys(draft),
          mergedKeys: Object.keys(mergedData)
        });
        setSubmissionFormData(mergedData);
      } else {
        console.log('⚠️ No draft found, starting fresh');
        // GUID in URL but no draft found - start fresh
        setSubmissionFormData(initialFormData);
      }
    } else {
      console.log('🆕 No session GUID in URL, checking for existing drafts...');
      // No GUID in URL - check if drafts exist for this form
      const existingDrafts = draftStorageService.listDraftsForForm(formId);

      if (existingDrafts.length > 0) {
        console.log('📦 Found existing drafts, showing modal');
        // Show modal to restore or start fresh
        const mostRecent = existingDrafts.sort((a, b) =>
          new Date(b.timestamp) - new Date(a.timestamp)
        )[0];

        console.log('📅 Most recent draft:', {
          sessionGuid: mostRecent.sessionGuid,
          timestamp: mostRecent.timestamp,
          dataKeys: Object.keys(mostRecent.data)
        });

        setDraftToRestore({ ...initialFormData, ...mostRecent.data });
        setDraftTimestampToShow(mostRecent.timestamp);
        setDraftSessionGuid(mostRecent.sessionGuid);
        setShowDraftModal(true);
      } else {
        console.log('🎯 No drafts found, generating new GUID and redirecting...');
        // No drafts - generate new GUID and redirect
        const newGuid = draftStorageService.generateSessionGuid();
        console.log('🔑 Generated new GUID:', newGuid);
        navigate(`/forms/${formId}/submissions/create/${newGuid}`, { replace: true });
      }

      // Initialize with empty data (will redirect or show modal)
      setSubmissionFormData(initialFormData);
    }
  }, [sessionGuid, formSchema, formId, navigate]);

  // Handle subform updates from navigation state
  useEffect(() => {
    console.log('🔄 SUBFORM UPDATE CHECK:', {
      hasSubformUpdate: !!routerLocation.state?.subformUpdate,
      hasSchema: formSchema.length > 0,
      sessionGuid
    });

    // Only process subform updates after schema is loaded and we have a session GUID
    if (routerLocation.state?.subformUpdate && formSchema.length > 0 && sessionGuid) {
      const { fieldId, instanceIndex, instanceData } = routerLocation.state.subformUpdate;

      // Create a unique key for this update to detect duplicates
      const updateKey = `${fieldId}_${instanceIndex}_${JSON.stringify(instanceData)}`;
      const SESSION_KEY = `processed_subform_${sessionGuid}`;

      // Check if we've already processed this exact update
      const processedKey = sessionStorage.getItem(SESSION_KEY);
      if (processedKey === updateKey) {
        console.log('⏭️ Skipping duplicate subform update:', updateKey);
        return;
      }

      console.log('📝 PROCESSING SUBFORM UPDATE:', {
        fieldId,
        instanceIndex,
        instanceDataKeys: Object.keys(instanceData),
        mode: instanceIndex !== null && instanceIndex !== undefined ? 'EDIT' : 'ADD'
      });

      // Mark this update as processed
      sessionStorage.setItem(SESSION_KEY, updateKey);
      console.log('✅ Marked update as processed:', updateKey);

      setSubmissionFormData(prev => {
        // prev should already have complete draft data loaded from draft check
        console.log('🔀 Merging subform update with state:', {
          prevKeys: Object.keys(prev),
          fieldId,
          currentInstanceCount: Array.isArray(prev[fieldId]) ? prev[fieldId].length : 0
        });

        const currentInstances = Array.isArray(prev[fieldId]) ? prev[fieldId] : [];

        let newInstances;
        if (instanceIndex !== null && instanceIndex !== undefined) {
          // Edit mode - replace existing instance
          newInstances = [...currentInstances];
          newInstances[instanceIndex] = instanceData;
          console.log(`✏️ Editing instance #${instanceIndex}`);
        } else {
          // Add mode - append new instance
          newInstances = [...currentInstances, instanceData];
          console.log(`➕ Adding new instance (total: ${newInstances.length})`);
        }

        const updatedFormData = { ...prev, [fieldId]: newInstances };

        console.log('💾 Immediately saving subform update to draft:', {
          keys: Object.keys(updatedFormData)
        });

        // Immediately save draft to avoid stale data
        draftStorageService.saveDraft(sessionGuid, formId, updatedFormData);

        return updatedFormData;
      });

      setIsDirty(true); // Mark as dirty after subform update

      // Clear state to prevent re-triggering
      navigate(routerLocation.pathname, { replace: true, state: {} });

      // Clear the processed update marker after a delay
      setTimeout(() => {
        sessionStorage.removeItem(SESSION_KEY);
        console.log('🧹 Cleared processed update marker');
      }, 100);
    }
  }, [routerLocation.state, navigate, routerLocation.pathname, formId, sessionGuid, formSchema]);

  useEffect(() => {
    const fetchFormData = async () => {
      try {
        setLoading(true);

        // Fetch form details
        const form = await formSchemaService.getFormSchemaById(formId);
        setFormData(form);

        // Convert schema definition to field array format
        let fields = [];
        if (form.schemaDefinition) {
          // Use new schema definition format
          fields = convertFormSchemaDefinitionToFields(form.schemaDefinition);
          console.log('Converted fields from schemaDefinition:', fields);
        } else if (form.schemaJson) {
          // Backward compatibility: handle old schemaJson format
          try {
            const parsedSchema = JSON.parse(form.schemaJson);
            fields = convertFormSchemaDefinitionToFields(parsedSchema);
            console.log('Converted fields from legacy schemaJson:', fields);
          } catch (error) {
            console.error('Failed to parse legacy schemaJson:', error);
            toast.error('Failed to parse form schema');
          }
        }

        setFormSchema(fields);

        // Fetch available work orders
        try {
          const woResponse = await workOrderService.getWorkOrders();
          const woData = Array.isArray(woResponse) ? woResponse
            : woResponse?.$values || woResponse?.items || [];
          setWorkOrders(woData);
        } catch (error) {
          console.error('Failed to fetch work orders:', error);
          // Non-critical, don't block form loading
        }

        // Request location
        requestLocation();
      } catch (error) {
        console.error('Failed to fetch form:', error);
        toast.error('Failed to load form');
        navigate(`/forms/${formId}/submissions`);
      } finally {
        setLoading(false);
      }
    };

    if (formId) {
      fetchFormData();
    }
  }, [formId, navigate]);

  // Handle draft restoration
  const handleRestoreDraft = () => {
    console.log('🔄 RESTORE DRAFT clicked:', {
      hasDraftToRestore: !!draftToRestore,
      draftSessionGuid,
      draftKeys: draftToRestore ? Object.keys(draftToRestore) : []
    });

    if (draftToRestore && draftSessionGuid) {
      // Redirect to URL with the draft's GUID
      const targetUrl = `/forms/${formId}/submissions/create/${draftSessionGuid}`;
      console.log('🔀 Redirecting to:', targetUrl);
      navigate(targetUrl, { replace: true });
      setShowDraftModal(false);
      // Data will load automatically via the draft check logic
    }
  };

  const handleStartFresh = () => {
    console.log('🆕 START FRESH clicked');

    // Delete the old draft if it exists
    if (draftSessionGuid) {
      console.log('🗑️ Deleting old draft:', draftSessionGuid);
      draftStorageService.deleteDraft(draftSessionGuid, formId);
    }

    // Generate new GUID and redirect
    const newGuid = draftStorageService.generateSessionGuid();
    console.log('🔑 Generated new GUID:', newGuid);
    navigate(`/forms/${formId}/submissions/create/${newGuid}`, { replace: true });
    setShowDraftModal(false);
  };

  // Callback to save draft immediately before navigation to subform (no debouncing)
  const handleBeforeNavigateToSubform = (sessionGuidParam, formDataToSave) => {
    console.log('🚀 Pre-navigation draft save triggered:', {
      sessionGuid: sessionGuidParam,
      formId,
      dataKeys: Object.keys(formDataToSave)
    });
    draftStorageService.saveDraft(sessionGuidParam, formId, formDataToSave);
    console.log('✅ Draft saved successfully before navigation');
  };

  // Generate a simple device ID
  const generateDeviceId = () => {
    const stored = localStorage.getItem('deviceId');
    if (stored) return stored;

    const deviceId = `web-${navigator.userAgent.substring(0, 50).replace(/[^a-zA-Z0-9]/g, '')}-${Date.now()}`;
    localStorage.setItem('deviceId', deviceId);
    return deviceId;
  };

  // Request geolocation
  const requestLocation = () => {
    setLocationLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
          setLocationLoading(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          toast.warning('Unable to get location. You can still submit without location.');
          setLocationLoading(false);
        }
      );
    } else {
      toast.warning('Geolocation is not supported by your browser.');
      setLocationLoading(false);
    }
  };

  // Handle form submission
  const handleSubmitForm = async () => {
    try {
      setSubmitting(true);
      setFieldErrors({});

      // Validate required fields. Fields hidden by an active visibility rule were
      // never shown to the user (and the backend exempts them), so skip them.
      const ruleCanonical = formSchema.map(f => ({
        key: f.id,
        options: f.props?.options,
        rules: f.props?.rules,
      }));
      const hiddenKeys = new Set(evaluateRules(ruleCanonical, submissionFormData).hiddenFieldKeys || []);
      const errors = {};
      formSchema.forEach(field => {
        if (hiddenKeys.has(field.id)) return;
        const fieldValue = submissionFormData[field.id];

        // Check required fields
        if (field.props?.required && !fieldValue) {
          errors[field.id] = `${field.props.label} is required`;
          return;
        }

        // Validate scanner fields with validation rules
        if ((field.type === 'qr_scanner' || field.type === 'barcode_scanner')
            && field.props?.validationRules
            && fieldValue) {
          const validation = validateField(fieldValue, field.props.validationRules);
          if (!validation.isValid) {
            errors[field.id] = validation.errors.join('; ');
          }
        }
      });

      if (Object.keys(errors).length > 0) {
        Object.values(errors).forEach(error => toast.error(error));
        setSubmitting(false);
        return;
      }

      // Calculate form duration
      const formDurationSeconds = Math.floor((Date.now() - formStartTime) / 1000);

      // Prepare typed submission data (API v3.1+)
      // File paths are already included in submissionFormData fields
      // Backend automatically detects media fields (Image/Drawing/Voice/File types)
      // and extracts temp paths from submissionData.fields[fieldKey]
      const typedSubmissionData = formSubmissionService.createSubmissionDataObject(
        submissionFormData,
        {
          clientVersion: '1.0.0',
          userAgent: navigator.userAgent,
          formDurationSeconds,
        }
      );

      const submissionPayload = {
        formSchemaId: parseInt(formId),
        submissionData: typedSubmissionData,
        // Submitter is editable only by admins when the org enables it; otherwise the current user.
        submitterName: canEditSubmitter
          ? submitterName
          : user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
        submitterEmail: canEditSubmitter ? submitterEmail : user?.email || '',
        latitude: location?.latitude,
        longitude: location?.longitude,
        locationName: location ? `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}` : null,
        deviceId: generateDeviceId(),
        isOfflineSubmission: false,
        // DeviceTimestamp = when user clicked submit (used for SubmittedAt). When the org allows
        // editing the date/time, use the admin-chosen value; the server ignores it otherwise.
        deviceTimestamp:
          allowEditSubmissionDateTime && submittedAtLocal
            ? new Date(submittedAtLocal).toISOString()
            : new Date().toISOString(),
        // FormStartedAt = when user opened the form (for duration tracking)
        formStartedAt: new Date(formStartTime).toISOString(),
        workOrderId: selectedWorkOrderId ? parseInt(selectedWorkOrderId) : null,
      };

      console.log('Submitting:', submissionPayload);

      await formSubmissionService.createSubmission(submissionPayload);

      toast.success('Submission created successfully');

      // Clear draft after successful submission
      if (sessionGuid) {
        draftStorageService.deleteDraft(sessionGuid, formId);
      }
      setIsDirty(false);

      navigate(`/forms/${formId}/submissions`);
    } catch (error) {
      console.error('Failed to create submission:', error);

      // The axios interceptor wraps errors as { message, status, data, originalError }
      const responseData = error.data;

      // Handle validation errors from API (v3.1+)
      if (responseData?.errors && Array.isArray(responseData.errors)) {
        // Show main validation message
        toast.error(responseData.message || 'Form submission validation failed');
        // Map field-level errors to highlight fields in the form
        const newFieldErrors = {};
        responseData.errors.forEach(err => {
          if (err.field) newFieldErrors[err.field] = err.message;
        });
        if (Object.keys(newFieldErrors).length > 0) {
          setFieldErrors(newFieldErrors);
        }
      } else {
        // Fallback to generic error message
        toast.error(responseData?.message || error.message || 'Failed to create submission');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/forms/${formId}/submissions`);
  };

  const handleSaveAsDraft = () => {
    console.log('💾 SAVE AS DRAFT clicked:', {
      sessionGuid,
      formId,
      submissionFormDataKeys: Object.keys(submissionFormData),
      isDirty
    });

    // Trigger an immediate save before navigating
    if (sessionGuid && Object.keys(submissionFormData).length > 0) {
      console.log('💾 Forcing immediate draft save before navigation');
      draftStorageService.saveDraft(sessionGuid, formId, submissionFormData);
    }

    // Draft is already auto-saved, just navigate back
    setIsDirty(false);
    toast.success('Draft saved successfully');
    navigate(`/forms/${formId}/submissions`);
  };

  if (loading) {
    return (
      <Layout currentPath={`/forms/${formId}/submissions/create`}>
        <LoadingContainer theme={theme}>
          <ThemedText>Loading form...</ThemedText>
        </LoadingContainer>
      </Layout>
    );
  }

  if (!enablePortalFormSubmission) {
    return (
      <Layout currentPath={`/forms/${formId}/submissions/create`}>
        <LoadingContainer theme={theme}>
          <ThemedText type="subtitle" style={{ marginBottom: theme.spacing.sm }}>
            Form submission via the portal is disabled
          </ThemedText>
          <ThemedText style={{ color: theme.colors.muted, marginBottom: theme.spacing.lg, textAlign: 'center' }}>
            Your organization has turned off creating submissions from the web portal.
            Submissions can still be collected from the mobile app.
          </ThemedText>
          <Button variant="primary" onClick={() => navigate(`/forms/${formId}/submissions`)}>
            Back to Submissions
          </Button>
        </LoadingContainer>
      </Layout>
    );
  }

  return (
    <Layout currentPath={`/forms/${formId}/submissions/create`}>
      <Helmet>
        <title>{`Create Submission - ${formData?.name || 'Form'} - aQRate Portal`}</title>
        <meta name="description" content={`Create a new submission for ${formData?.name || 'this form'}`} />
      </Helmet>

      <PageContainer theme={theme}>
        {formData && (
          <FormInfoBanner theme={theme}>
            <FormIconLarge>{renderFormIcon(formData.icon, 32)}</FormIconLarge>
            <div style={{ flex: 1 }}>
              <ThemedText type="title" style={{ color: 'white', marginBottom: theme.spacing.xs }}>
                {formData.name}
              </ThemedText>
              <ThemedText style={{ color: 'rgba(255,255,255,0.9)' }}>
                Create New Submission
              </ThemedText>
            </div>
          </FormInfoBanner>
        )}

        <PageHeader theme={theme}>
          <HeaderLeft>
            <ThemedText type="subtitle">
              Fill out the form below
            </ThemedText>
            <ThemedText style={{ color: theme.colors.muted }}>
              All fields marked with * are required
            </ThemedText>
          </HeaderLeft>

          <HeaderRight theme={theme}>
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
          </HeaderRight>
        </PageHeader>

        {/* Submission Information */}
        <InfoBox theme={theme}>
          <ThemedText type="defaultSemiBold" style={{ marginBottom: theme.spacing.sm }}>
            Submission Information
          </ThemedText>
          <InfoRow theme={theme}>
            <ThemedText type="small">Submitter:</ThemedText>
            {canEditSubmitter ? (
              <MetaInput
                theme={theme}
                type="text"
                value={submitterName}
                onChange={(e) => setSubmitterName(e.target.value)}
                placeholder="Submitter name"
              />
            ) : (
              <ThemedText type="small" style={{ fontWeight: 600 }}>
                {user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Anonymous'}
              </ThemedText>
            )}
          </InfoRow>
          <InfoRow theme={theme}>
            <ThemedText type="small">Email:</ThemedText>
            {canEditSubmitter ? (
              <MetaInput
                theme={theme}
                type="email"
                value={submitterEmail}
                onChange={(e) => setSubmitterEmail(e.target.value)}
                placeholder="Submitter email"
              />
            ) : (
              <ThemedText type="small" style={{ fontWeight: 600 }}>
                {user?.email || 'N/A'}
              </ThemedText>
            )}
          </InfoRow>
          <InfoRow theme={theme}>
            <ThemedText type="small">Location:</ThemedText>
            <ThemedText type="small" style={{ fontWeight: 600 }}>
              {locationLoading
                ? 'Getting location...'
                : location
                ? `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`
                : 'Not available'}
            </ThemedText>
          </InfoRow>
          {allowEditSubmissionLocation ? (
            // Editable: draggable pin (drag, click the map, or use device GPS).
            <LocationEditorMap
              theme={theme}
              latitude={location?.latitude ?? null}
              longitude={location?.longitude ?? null}
              onChange={(lat, lng, accuracy) =>
                setLocation({ latitude: lat, longitude: lng, accuracy: accuracy ?? null })
              }
            />
          ) : (
            <>
              {location && (
                <>
                  {location.accuracy != null && (
                    <InfoRow theme={theme}>
                      <ThemedText type="small">Accuracy:</ThemedText>
                      <ThemedText type="small" style={{ fontWeight: 600 }}>
                        ±{location.accuracy.toFixed(0)}m
                      </ThemedText>
                    </InfoRow>
                  )}
                  <MiniMap theme={theme}>
                    <iframe
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      scrolling="no"
                      marginHeight="0"
                      marginWidth="0"
                      src={`https://www.openstreetmap.org/export/embed.html?bbox=${location.longitude - 0.01},${location.latitude - 0.01},${location.longitude + 0.01},${location.latitude + 0.01}&layer=mapnik&marker=${location.latitude},${location.longitude}`}
                      style={{ border: 'none' }}
                      title="Location Map"
                    />
                  </MiniMap>
                  <div style={{ marginTop: theme.spacing.xs }}>
                    <MapLinksDropdown
                      latitude={location.latitude}
                      longitude={location.longitude}
                      label="Open in Maps"
                    />
                  </div>
                </>
              )}
              {!location && !locationLoading && (
                <MiniMap theme={theme}>
                  <MapPlaceholder theme={theme}>
                    <div style={{ fontSize: '32px', marginBottom: theme.spacing.sm }}><MapPin size={32} /></div>
                    <ThemedText type="small">Location not available</ThemedText>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={requestLocation}
                      style={{ marginTop: theme.spacing.md }}
                    >
                      Retry Location
                    </Button>
                  </MapPlaceholder>
                </MiniMap>
              )}
            </>
          )}
          <InfoRow theme={theme}>
            <ThemedText type="small">Device:</ThemedText>
            <ThemedText type="small" style={{ fontWeight: 600 }}>
              Web Browser
            </ThemedText>
          </InfoRow>
          <InfoRow theme={theme}>
            <ThemedText type="small">Work Order:</ThemedText>
            <div style={{ flex: 1, maxWidth: '300px' }}>
              <WorkOrderSelect
                theme={theme}
                value={selectedWorkOrderId}
                onChange={(e) => setSelectedWorkOrderId(e.target.value)}
              >
                <option value="">None</option>
                {workOrders.map(wo => (
                  <option key={wo.id} value={wo.id}>
                    {wo.workOrderNumber || `WO-${wo.id}`}: {wo.title}
                  </option>
                ))}
              </WorkOrderSelect>
            </div>
          </InfoRow>
          <InfoRow theme={theme}>
            <ThemedText type="small">Timestamp:</ThemedText>
            {allowEditSubmissionDateTime ? (
              <MetaInput
                theme={theme}
                type="datetime-local"
                value={submittedAtLocal}
                onChange={(e) => setSubmittedAtLocal(e.target.value)}
              />
            ) : (
              <ThemedText type="small" style={{ fontWeight: 600 }}>
                {new Date().toLocaleString()}
              </ThemedText>
            )}
          </InfoRow>
        </InfoBox>

        {/* Dynamic Form */}
        <FormCard theme={theme}>
          <DynamicFormRenderer
            schema={formSchema}
            formData={submissionFormData}
            onChange={setSubmissionFormData}
            errors={fieldErrors}
            onBeforeNavigate={handleBeforeNavigateToSubform}
            formSchemaId={parseInt(formId)}
          />

          <ButtonGroup theme={theme}>
            <Button
              variant="secondary"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={handleSaveAsDraft}
              disabled={submitting || !sessionGuid}
            >
              <Save size={14} /> Save as Draft
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmitForm}
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit Form'}
            </Button>
          </ButtonGroup>
        </FormCard>
      </PageContainer>

      {/* Draft Restoration Modal */}
      {showDraftModal && (
        <DraftModalOverlay theme={theme} onClick={handleStartFresh}>
          <DraftModal theme={theme} onClick={(e) => e.stopPropagation()}>
            <DraftModalHeader theme={theme}>
              <DraftIcon><Save size={16} /></DraftIcon>
              <div>
                <ThemedText type="title">Draft Found</ThemedText>
                <ThemedText type="small" style={{ color: theme.colors.muted }}>
                  {draftTimestampToShow
                    ? `Last saved: ${new Date(draftTimestampToShow).toLocaleString()}`
                    : 'A previous draft was found'}
                </ThemedText>
              </div>
            </DraftModalHeader>
            <DraftModalBody theme={theme}>
              <ThemedText>
                We found a saved draft for this form. Would you like to restore it and continue where you left off?
              </ThemedText>
              <InfoText theme={theme}>
                <Info size={14} /> Note: Images, files, and signatures will need to be re-uploaded/captured.
              </InfoText>
            </DraftModalBody>
            <DraftModalFooter theme={theme}>
              <Button variant="secondary" onClick={handleStartFresh}>
                Start Fresh
              </Button>
              <Button variant="primary" onClick={handleRestoreDraft}>
                Restore Draft
              </Button>
            </DraftModalFooter>
          </DraftModal>
        </DraftModalOverlay>
      )}
    </Layout>
  );
};

export default CreateSubmissionPage;
